import { Typography } from '@euler/app/components/typography';
import { SiteIcon } from '@euler/app/flows/task/components/SiteIcon';
import { Icon } from '@euler/components/Icomoon';
import {
  getDefectiveLevelBgColor,
  getDefectiveLevelColor,
} from '@euler/functions/getDefectiveLevel';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { SiteGirdItemTag } from '@euler/typings/styled';
import { MotiView } from 'moti';
import { FC, ReactNode, memo, useCallback, useMemo } from 'react';
import { StyleProp, TouchableOpacity, View, ViewStyle } from 'react-native';
import { css, useTheme } from 'styled-components';
import * as InspectionSiteGrid from '../functions/InspectionSiteGrid';

const kDefaultNumberOfColumns = 4;
const kCellContentOffset = 0.5;
const kDefaultCellSize = 107;

type SiteGridCellInfo = (
  | { type: 'placeholder' }
  | (InspectionSiteGrid.ItemInfo & {
      type: 'item';
      end?: boolean;
    })
) & { first?: boolean; isFirstRow?: boolean; isLastRow?: boolean };

const Row: FC<{ children?: ReactNode }> = memo(({ children }) => {
  return (
    <View
      css={`
        flex-direction: row;
        flex-wrap: nowrap;
        justify-content: flex-start;
        align-items: stretch;
      `}
    >
      {children}
    </View>
  );
});

const TagIcon = memo(
  ({
    tag,
    size,
    style,
  }: {
    tag: SiteGirdItemTag;
    size?: number;
    style?: StyleProp<ViewStyle>;
  }) => {
    const { rpx } = useResponsiveContext();
    size = size ?? rpx(13);
    if (tag === 'hardware-capable') {
      return <Icon name="bluetooth-o" size={size} color="#fff" style={style} />;
    }
    if (tag === 'with-pending-issue') {
      return (
        <Typography.Label color="#fff" size={size - 2} style={style}>
          遗
        </Typography.Label>
      );
    }
    if (tag === 'service') {
      return (
        <Typography.Label color="#fff" size={size - 2} style={style}>
          保
        </Typography.Label>
      );
    }
    if (tag === 'required') {
      return (
        <Typography.Label color="#fff" size={size - 2} style={style}>
          必
        </Typography.Label>
      );
    }
    return null;
  },
);

// http://apps.eky.hk/css-triangle-generator/
const Badge: FC<{
  size: number;
  color: string;
  tag: SiteGirdItemTag;
  style?: StyleProp<ViewStyle>;
}> = memo(({ size, color, tag, style }) => (
  <View
    css={`
      position: absolute;
      left: 0;
      top: 0;
      width: ${size}px;
      height: ${size}px;
    `}
    style={style}
  >
    <View
      css={`
        width: 0px;
        height: 0px;
        border-top-width: ${size}px;
        border-right-width: ${size}px;
        border-bottom-width: 0px;
        border-left-width: 0px;
        border-top-color: ${color};
        border-right-color: transparent;
        border-bottom-color: transparent;
        border-left-color: transparent;
        position: absolute;
        left: 0;
        top: 0;
      `}
    />
    <TagIcon
      tag={tag}
      size={size * 0.45}
      css={`
        z-index: 1;
        transform: translateX(${(size * 0.25) / 3}px)
          translateY(${-(size * 0.25) / 20}px);
      `}
    />
  </View>
));

const Cell = memo(
  ({
    item,
    size,
    showPlaceholder,
    onPress,
  }: {
    item: SiteGridCellInfo;
    size: number;
    showPlaceholder?: boolean;
    onPress?: (item: InspectionSiteGrid.ItemInfo) => void;
  }) => {
    const theme = useTheme();
    const { rpx } = useResponsiveContext();

    const onCellPress = useCallback(() => {
      if (item.type === 'placeholder') return;
      onPress?.(item);
    }, [item, onPress]);

    if (item.type === 'placeholder') {
      return (
        <View
          css={`
            width: ${size}px;
            height: ${size}px;
            flex-grow: 0;
            flex-shrink: 0;
            background-color: ${showPlaceholder !== false
              ? theme.inspection.siteGrid.cellBgColor
              : 'transparent'};
            ${showPlaceholder !== false
              ? css`
                  border-bottom-width: 1px;
                  border-bottom-color: ${theme.inspection.siteGrid.borderColor};
                `
              : ''};
            ${item.first || showPlaceholder !== false
              ? css`
                  border-left-width: 1px;
                  border-left-color: ${theme.inspection.siteGrid.borderColor};
                `
              : ''};
            ${showPlaceholder !== false && item.isFirstRow
              ? css`
                  border-top-width: 1px;
                  border-top-color: ${theme.inspection.siteGrid.borderColor};
                `
              : ''};
          `}
        />
      );
    }

    const { first, isFirstRow } = item;

    const backgroundColor = item.defectiveLevel
      ? getDefectiveLevelBgColor(theme, item.defectiveLevel)
      : undefined;

    return (
      <View
        css={`
          width: ${size}px;
          height: ${size}px;
          align-items: center;
          justify-content: center;
          flex-grow: 0;
          flex-shrink: 0;
          background-color: ${theme.inspection.siteGrid.cellBgColor};
          border-bottom-width: 1px;
          border-bottom-color: ${theme.inspection.siteGrid.borderColor};
          ${isFirstRow
            ? css`
                border-top-width: 1px;
                border-top-color: ${theme.inspection.siteGrid.borderColor};
              `
            : ''};
          ${first
            ? ''
            : css`
                border-left-width: 1px;
                border-left-color: ${theme.inspection.siteGrid.borderColor};
              `};
        `}
      >
        {backgroundColor ? (
          <MotiView
            from={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            css={`
              background-color: ${backgroundColor};
              position: absolute;
              top: ${kCellContentOffset}px;
              left: ${kCellContentOffset}px;
              right: ${kCellContentOffset}px;
              bottom: ${kCellContentOffset}px;
            `}
          />
        ) : null}
        <TouchableOpacity
          css={`
            position: absolute;
            top: ${kCellContentOffset}px;
            left: ${kCellContentOffset}px;
            right: ${kCellContentOffset}px;
            bottom: ${kCellContentOffset}px;
            align-items: center;
            justify-content: center;
          `}
          onPress={onCellPress}
        >
          <SiteIcon
            url={item.icon}
            size={Math.round(size * 0.45)}
            type={item.iconType}
            color={theme.inspection.siteGrid.siteIconColor}
          />
          <Typography.Label
            light
            size={Math.min(rpx(20), rpx(14) * (size / rpx(kDefaultCellSize)))}
            align="center"
            color={
              item.defectiveLevel
                ? getDefectiveLevelColor(theme, item.defectiveLevel)
                : theme.inspection.siteGrid.defaultSiteNameColor
            }
          >
            {item.name}
          </Typography.Label>
        </TouchableOpacity>
        {item.tag ? (
          <Badge
            color={theme.inspection.siteGrid.tagColors[item.tag]}
            size={size * 0.25}
            tag={item.tag}
          />
        ) : null}
      </View>
    );
  },
);

export const InspectionSiteGridView = memo(
  ({
    items,
    width,
    numberOfColumns = kDefaultNumberOfColumns,
    showPlaceholder,
    onCellPress,
  }: {
    items: InspectionSiteGrid.ItemInfo[];
    width: number;
    numberOfColumns?: number;
    showPlaceholder?: boolean;
    onCellPress?: (item: InspectionSiteGrid.ItemInfo) => void;
  }) => {
    const theme = useTheme();
    const rows = useMemo(() => {
      const list: Array<SiteGridCellInfo[]> = [];
      const rowCount = Math.floor((items.length - 1) / numberOfColumns) + 1;

      let i = 0;
      while (i < items.length) {
        const slice = items.slice(i, i + numberOfColumns);
        list.push(
          slice.map((x, k) => ({
            type: 'item',
            ...x,
            first: k === 0,
            end: k === slice.length - 1 && slice.length < numberOfColumns,
            isFirstRow: list.length === 0,
            isLastRow: list.length === rowCount - 1,
          })),
        );
        i += numberOfColumns;
      }

      const lastRow = list[list.length - 1];
      if (lastRow) {
        let beginPlaceholder = true;
        while (lastRow.length < numberOfColumns) {
          lastRow.push({
            type: 'placeholder',
            first: beginPlaceholder,
            isFirstRow: list.length === 1,
            isLastRow: list.length === 1,
          });
          beginPlaceholder = false;
        }
      }

      return list;
    }, [items, numberOfColumns]);
    const size = Math.round(width / numberOfColumns);
    return (
      <View
        css={`
          //background-color: ${theme.inspection.siteGrid.cellBgColor};
        `}
      >
        {rows.map((row, i) => (
          <Row key={i}>
            {row.map((item, j) => (
              <Cell
                showPlaceholder={showPlaceholder}
                item={item}
                key={
                  item.type === 'item'
                    ? item.customIssueId ?? item.siteId
                    : `placeholder-${i}-${j}`
                }
                size={size}
                onPress={onCellPress}
              />
            ))}
          </Row>
        ))}
      </View>
    );
  },
);
